let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  variantChange: 'variant-change',
  cartError: 'cart-error',
  glideUpdate: 'glide-update',
  mediaQueryChange: 'media-query-change'
};

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

if (typeof p80 === 'undefined') {
  var p80 = {};
}

/***
 *
 *  Media Query Listener
 *
 *  Used for targetting specific breakpoints for devices via JS.
 *  Because Shopify has updated the viewport tag in the latest Dawn `window.innerWidth` etc are no longer reliable on mobile devices for exact pixel comparisons
 *  Instead we need to match media breakpoints to determine what size device is viewing
 *
 *  You can see just below the class definition for `MediaQueryListener` that I have instanced this globally as `p80.mediaQueryService`
 *  `p80.mediaQueryService = new MediaQueryListener();`
 *  Should you ever need to directly access the current breakpoint you can do so via `p80.mediaQueryService.currentMediaQuery`
 *  However the intent is that you will use this in combination with the new event listener system that has been integrated
 *  Accessing the instance directly is only to cover edge cases and initalization
 *
 *  You can view an example of how this works via `ProductSlider`
 *
 */

class MediaQueryListener {
  constructor() {
    this.mediaTypes = {
      small: 'small',
      medium: 'medium',
      large: 'large',
      touch: 'touch'
    };
    this.mediaQueries = [
      {query: window.matchMedia('(max-width: 749px)'), type: 'small'},
      {
        query: window.matchMedia('(min-width: 750px) and (max-width: 989px)'),
        type: 'medium'
      },
      {query: window.matchMedia('(min-width: 990px)'), type: 'large'},
      {query: window.matchMedia('(pointer: coarse)'), type: 'touch'}
    ];

    this.touchSupport = window.matchMedia('(pointer: coarse)').matches;

    this.queryChangeHandler = this.onQueryChange.bind(this);

    this.currentMediaQuery = this.mediaQueries.find((queryOption) => {
      return queryOption.query.matches && queryOption.query.type !== this.mediaTypes.touch;
    });

    this.mediaQueries.forEach((queryOption) => {
      queryOption.query.addEventListener('change', this.queryChangeHandler.bind(this, queryOption));
    });

    document.addEventListener('DOMContentLoaded', this.publish());
  }

  onQueryChange(queryOption) {
    if (
      queryOption &&
      queryOption.query &&
      queryOption.query.matches &&
      this.currentMediaQuery.type === queryOption.type
    ) {
      return;
    }

    if (queryOption.type !== this.mediaTypes.touch) {
      const previousMedia = this.currentMediaQuery;
      const newMedia = this.mediaQueries.find((queryOption) => {
        return queryOption.query.matches;
      });
      if (previousMedia && previousMedia.type === newMedia.type) return;
      this.currentMediaQuery = newMedia;
    } else {
      this.touchSupport = queryOption.query.matches;
    }

    this.publish();
  }

  publish() {
    if (typeof publish === 'undefined') return;
    publish(PUB_SUB_EVENTS.mediaQueryChange, {
      source: 'MediaQueryListener',
      currentMedia: this.currentMediaQuery,
      touchSupport: this.touchSupport
    });
  }
}

p80.mediaQueryService = new MediaQueryListener();

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

let menuLeaveTimer;
let menuEnterTimer;

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);

  if (summary.parentNode.classList.contains('mega-menu')) {
    summary.parentNode.addEventListener('mouseleave', function () {
      clearTimeout(menuEnterTimer);
      menuLeaveTimer = setTimeout(() => {
        summary.parentNode.removeAttribute('open');
        summary.setAttribute('aria-expanded', false);
      }, 300);
    });

    summary.parentNode.addEventListener(
      'mouseenter',
      debounce((event) => {
        clearTimeout(menuEnterTimer);
        clearTimeout(menuLeaveTimer);
        if (!summary.parentNode.getAttribute('open')) {
          menuEnterTimer = setTimeout(() => {
            const parent = summary.parentNode.closest('.list-menu');
            const menus = parent?.querySelectorAll('details');
            menus.forEach((menu) => {
              menu.removeAttribute('open');
              menu.querySelector('summary')?.setAttribute('aria-expanded', false);
            });
            summary.parentNode.setAttribute('open', true);
            summary.setAttribute('aria-expanded', true);
          }, 300);
        }
      }, 10)
    );
  }
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN'
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.currentQuantity = this.input ? parseInt(this.input.value, 10) : 1;
    this.quantityIncrement = this.hasAttribute('data-increment') ? parseInt(this.getAttribute('data-increment')) : 1;
    this.quantityMinimum = this.hasAttribute('data-minimum') ? parseInt(this.getAttribute('data-minimum')) : 1;
    this.quantityMaximum = this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : 9999;
    this.changeEvent = new Event('change', {bubbles: true});

    this.input.addEventListener('change', (event) => {
      if (this.input.value > this.quantityMinimum) {
        this.input.value = Math.round(this.input.value / this.quantityIncrement) * this.quantityIncrement;
      } else {
        this.input.value = this.quantityMinimum;
      }
    });

    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    if (event.target.name === 'plus' && this.currentQuantity + this.quantityIncrement > this.quantityMaximum) return;
    if (event.target.name === 'minus' && this.currentQuantity - this.quantityIncrement < this.quantityMinimum) return;

    const previousValue = this.input.value;

    event.target.name === 'plus'
      ? this.input.stepUp(this.quantityIncrement)
      : this.input.stepDown(this.quantityIncrement);

    if (previousValue !== this.input.value) {
      this.currentQuantity = this.input.value;
      this.input.dispatchEvent(this.changeEvent);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {'Content-Type': 'application/json', Accept: `application/${type}`}
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    if (navigator.platform === 'iPhone')
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.bindResize();
  }

  bindResize() {
    window.addEventListener(
      'resize',
      debounce((event) => {
        if (!this.header) return;
        if (window.innerWidth >= 990 || !this.header.classList.contains('menu-open')) {
          document.getElementById('menu-drawer').style.removeProperty('height');
        } else {
          let offsetHeight = this.header.querySelector('.header').offsetHeight;
          const announcements = document.getElementById('shopify-section-announcement-bar');
          if (announcements) {
            offsetHeight += announcements.offsetHeight;
          }
          const navHeight = window.innerHeight - offsetHeight;
          document.getElementById('menu-drawer').style.height = `${navHeight}px`;
        }
      }, 50)
    );
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.getElementById('shopify-section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    if (window.innerWidth >= 990) return;
    let offsetHeight = this.header.querySelector('.header').offsetHeight;
    const announcements = document.getElementById('shopify-section-announcement-bar');
    if (announcements) {
      offsetHeight += announcements.offsetHeight;
    }
    const navHeight = window.innerHeight - offsetHeight;
    document.getElementById('menu-drawer').style.height = `${navHeight}px`;
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    document.getElementById('menu-drawer').style.removeProperty('height');
  }
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  bindSection(sectionID) {
    this.slider = this.querySelector(sectionID + ' .slider');
    if (!this.slider) {
      return;
    }
    this.sliderItems = this.slider.querySelectorAll('.slider__slide');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(sectionID + ' .slider-counter--current');
    this.pageTotalElement = this.querySelector(sectionID + ' .slider-counter--total');
    this.prevButton = this.querySelector(sectionID + ' button[name="previous"]');
    this.nextButton = this.querySelector(sectionID + ' button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
    this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.slider.querySelector('.slideshow__slide').clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const button = item.querySelector('a');
      if (index === this.currentPage - 1) {
        if (button) button.removeAttribute('tabindex');
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (button) button.setAttribute('tabindex', '-1');
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateMetafieldsData();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
      this.updateSku();
      this.updateStockStatus();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateSku() {
    if (!this.currentVariant) return;
    const sku = document.querySelector('[data-product-sku]');
    if (!sku) return;
    sku.textContent = this.currentVariant.sku;
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', {bubbles: true}));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;
    if (typeof pickUpAvailability.fetchAvailability === typeof undefined) return;
    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  updateMetafieldsData() {
    if (!this.currentVariant) return;
    if (typeof p80 === typeof undefined) return;
    if (typeof p80.variantMeta === typeof undefined) return;
    const variantId = this.currentVariant.id;

    //update price breaks
    if (p80.variantMeta[variantId].price_break_html !== '') {
      this.setPriceBreak(p80.variantMeta[variantId]);
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  updateStockStatus() {
    if (!this.currentVariant) return;
    if (typeof p80 === typeof undefined) return;
    if (typeof p80.variantMeta === typeof undefined) return;
    const variant_id = this.currentVariant.id;

    let variant = this.currentVariant;
    let variant_meta = p80.variantMeta[variant_id];

    let is_in_stock = false;
    let is_preorder = false;
    let product_eta = variant_meta.eta;
    let stock_status = '';
    let stock_text = '';
    let stock_type = '';
    let show_eta = false;
    let badge_elem = document.querySelector('.product__info-container .product-in-stock');

    if (variant.available) {
      is_in_stock = true;
    }
    if (variant_meta.is_preorder == 'T') {
      is_preorder = true;
    }

    if (
      (is_in_stock && is_preorder) ||
      (variant_meta.inventory_policy === 'continue' && variant_meta.inventory_status === 'out of stock')
    ) {
      show_eta = true;
    }

    if (variant.available == false) {
      badge_elem.classList.add('soldout');
      badge_elem.querySelector('span').innerText = 'Check Back Soon';
      show_eta = true;
    } else if (is_in_stock) {
      badge_elem.classList.remove('soldout');
      if (is_preorder) {
        badge_elem.querySelector('span').innerText = 'Pre-Order Today!';
      } else if (variant_meta.inventory_status == 'low') {
        badge_elem.querySelector('span').innerText = 'LOW INVENTORY';
        badge_elem.classList.add('soldout');
      } else {
        badge_elem.querySelector('span').innerText = 'In Stock';
      }
    }

    let shipping_eta_elem = document.querySelector('.product__info-container .ship-container');
    if (shipping_eta_elem) {
      shipping_eta_elem.remove();
    }

    let shipping_eta_lip_input = document.getElementById('ShippingEtaLabel');
    if (shipping_eta_lip_input) {
      shipping_eta_lip_input.remove();
    }
    if (product_eta !== '' && p80.shipping_eta_label && show_eta === true) {
      shipping_eta_elem = document.createElement('div');
      shipping_eta_elem.classList.add('ship-container');
      shipping_eta_elem.innerHTML = 'Available to Ship After: ' + product_eta + '. Order today to reserve yours!';
      let priceContainer = document.querySelector('.product__info-container .main-price-container');
      priceContainer.parentNode.insertBefore(shipping_eta_elem, priceContainer);

      shipping_eta_lip_input = document.createElement('input');
      shipping_eta_lip_input.id = 'ShippingEtaLabel';
      shipping_eta_lip_input.value = product_eta;
      shipping_eta_lip_input.setAttribute('type', 'hidden');
      shipping_eta_lip_input.setAttribute('name', 'properties[' + p80.shipping_eta_label + ']');
      let productButtonsContainer = document.querySelector('.product-form__buttons');
      productButtonsContainer.parentNode.insertBefore(shipping_eta_lip_input, productButtonsContainer);
    }
  }

  setPriceBreak(variantMeta) {
    if (document.querySelector('.mix-and-match')) {
      document.querySelector('.mix-and-match').remove();
    }
    const priceBreakContainer = document.createElement('div');
    priceBreakContainer.innerHTML = variantMeta.price_break_html;
    document
      .querySelector('.main-price-container')
      .parentNode.insertBefore(priceBreakContainer, document.querySelector('.main-price-container'));

    if (document.getElementById('quantity_discount')) {
      document.getElementById('quantity_discount').remove();
    }

    if (variantMeta.price_breaks !== '') {
      const priceBreakInput = document.createElement('input');
      priceBreakInput.setAttribute('type', 'hidden');
      priceBreakInput.setAttribute('name', 'properties[_price_breaks]');
      priceBreakInput.setAttribute('id', 'quantity_discount');
      priceBreakInput.setAttribute('value', variantMeta.price_breaks);
      document
        .querySelector('.product-form__buttons')
        .parentNode.insertBefore(priceBreakInput, document.querySelector('.product-form__buttons'));
    }
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${
        this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        if (source && destination) destination.innerHTML = source.innerHTML;

        const addButtonPriceSource = html.getElementById(
          `add-button-price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );

        const addButtonPriceDestination = document.getElementById(`add-button-price-${this.dataset.section}`);
        if (addButtonPriceSource && addButtonPriceDestination) {
          addButtonPriceDestination.innerHTML = addButtonPriceSource.innerHTML;
        } else if (addButtonPriceDestination) {
          addButtonPriceDestination.innerHTML = '';
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);

        const freeShippingBadgeSource = html.querySelector('[data-free-shipping-badge]');
        const freeShippingBadgeDestination = document.querySelector('[data-free-shipping-badge]');
        if (freeShippingBadgeSource && freeShippingBadgeDestination) {
          freeShippingBadgeDestination.innerHTML = freeShippingBadgeSource.innerHTML;
        } else {
          freeShippingBadgeDestination.innerHTML = '';
        }

        const freeShippingCalloutSource = html.querySelector('[data-free-shipping-callout]');
        const freeShippingCalloutDestination = document.querySelector('[data-free-shipping-callout]');
        if (freeShippingCalloutSource && freeShippingCalloutDestination) {
          freeShippingCalloutDestination.innerHTML = freeShippingCalloutSource.innerHTML;
        } else {
          freeShippingCalloutDestination.innerHTML = '';
        }

        const shippingInformationSource = html.querySelector('[data-shipping-information]');
        const shippingInformationDestination = document.querySelector('[data-shipping-information]');
        if (shippingInformationSource && shippingInformationDestination) {
          shippingInformationDestination.innerHTML = shippingInformationSource.innerHTML;
        }

        const shippingDisclaimerSource = html.querySelector('[data-shipping-disclaimer]');
        const shippingDisclaimerDestination = document.querySelector('[data-shipping-disclaimer]');
        if (shippingDisclaimerSource && shippingDisclaimerDestination) {
          shippingDisclaimerDestination.innerHTML = shippingDisclaimerSource.innerHTML;
        } else {
          shippingDisclaimerDestination.innerHTML = '';
        }

        const formActionSource = html.querySelector('[data-product-form-action]');
        const formActionDestination = document.querySelector('[data-product-form-action]');
        if (formActionSource && formActionDestination) {
          formActionDestination.innerHTML = formActionSource.innerHTML;
        } else {
          formActionDestination.innerHTML = '';
        }
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class JumpLink extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.target = this.getAttribute('data-target');
    this.select = this.querySelector('select');
    if (this.select) {
      this.select.addEventListener('change', this.onChange.bind(this));
    } else {
      this.addEventListener('click', this.onClick.bind(this));
    }
  }
  getHeaderOffset() {
    const header = document.querySelector('sticky-header.header-wrapper');
    const headerOffset = header ? header.scrollHeight : 0;
    return headerOffset;
  }
  onClick() {
    if (!this.target) return;
    const scrollTarget = document.querySelector('[id="' + this.target + '"]');
    if (!scrollTarget) return;
    const targetOffset = scrollTarget.getBoundingClientRect().top + window.scrollY - this.getHeaderOffset();
    window.scrollTo({top: targetOffset, behavior: 'smooth'});
  }
  onChange() {
    this.target = this.select.value;
    if (!this.target) return;
    if (this.target.includes('/')) {
      document.location = this.target;
      return;
    }
    const scrollTarget = document.querySelector('[id="' + this.target + '"]');
    if (!scrollTarget) return;
    const targetOffset = scrollTarget.getBoundingClientRect().top + window.scrollY - this.getHeaderOffset();
    window.scrollTo({top: targetOffset, behavior: 'smooth'});
  }
}

customElements.define('jump-link', JumpLink);

/***
 *
 * Free Shipping Threshold Bar
 *
 * @param {number} (required) data-threshold - the value (in cents) required to hit the free shipping threshold
 * @param {number} (optional) data-total - the total value of items in the cart on initialization (if available)
 *
 */

class FreeShippingBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // The current shop currency
    this.currency = Shopify && Shopify.currency ? Shopify.currency.active : null;
    if (!this.currency) {
      console.error('Currency code not specified.');
      return;
    }
    // Free shipping threshold has not been met messaging
    this.freeShippingPending = this.querySelector('[data-free-shipping-pending]');
    // The current cart total within the pending messaging
    this.freeShippingPendingTotal = this.freeShippingPending
      ? this.freeShippingPending.querySelector('[data-amount]')
      : null;
    // Free shipping threshold has been met messaging
    this.freeShippingMet = this.querySelector('[data-free-shipping-met]');
    // The progress bar
    this.progress = this.querySelector('progress');
    // The free shipping threshold
    this.threshold = this.getAttribute('data-threshold');
    if (this.threshold) this.threshold = parseInt(this.threshold, 10);
    // The total price of the cart
    this.cartTotal = this.getAttribute('data-total');
    if (this.cartTotal) this.cartTotal = parseInt(this.cartTotal, 10);

    this.updateShipping();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event.cartData || typeof event.cartData.total_price === 'undefined') return;
      this.cartTotal = event.cartData.total_price;
      this.updateShipping();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  updateShipping() {
    if (typeof this.threshold === 'undefined' || this.threshold === null) return;

    const getLang = () => {
      if (navigator.languages != undefined) return navigator.languages[0];
      return navigator.language;
    };

    if (typeof this.cartTotal === 'undefined' || this.cartTotal === null) {
      let defaultPrice = this.threshold;
      defaultPrice = defaultPrice / 100;
      defaultPrice = defaultPrice.toLocaleString(getLang(), {
        style: 'currency',
        currency: this.currency
      });
      if (this.freeShippingPendingTotal) this.freeShippingPendingTotal.textContent = defaultPrice;
      if (this.progress) this.progress.setAttribute('value', 0);
      return;
    }
    if (this.cartTotal >= this.threshold) {
      if (this.freeShippingPending) this.freeShippingPending.classList.add('hidden');
      if (this.freeShippingMet) this.freeShippingMet.classList.remove('hidden');
    } else {
      if (this.freeShippingPending) this.freeShippingPending.classList.remove('hidden');
      if (this.freeShippingMet) this.freeShippingMet.classList.add('hidden');
    }
    if (this.progress) this.progress.setAttribute('value', this.cartTotal);
    if (this.freeShippingPendingTotal) this.freeShippingPendingTotal.textContent = this.formatThreshold();
  }

  formatThreshold() {
    if (typeof this.cartTotal === 'undefined' || this.cartTotal === null || !this.currency) return;

    const getLang = () => {
      if (navigator.languages != undefined) return navigator.languages[0];
      return navigator.language;
    };

    let price = this.threshold - this.cartTotal;
    if (price < 0) price = 0;
    price = price / 100;
    price = price.toLocaleString(getLang(), {
      style: 'currency',
      currency: this.currency
    });
    return price;
  }
}

customElements.define('free-shipping-bar', FreeShippingBar);

const bindCollapseMobileDetails = () => {
  const collapseDetails = document.querySelectorAll('[data-collapse-mobile]');

  handleCollapseMobileDetails(collapseDetails);

  window.addEventListener(
    'resize',
    debounce((event) => {
      handleCollapseMobileDetails(collapseDetails);
    }, 300)
  );
};

const handleCollapseMobileDetails = (collapseDetails) => {
  if (!collapseDetails) return;
  if (window.innerWidth < 750) {
    collapseDetails.forEach((details) => {
      details.removeAttribute('open');
      const summary = details.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', false);
      }
    });
  } else {
    collapseDetails.forEach((details) => {
      details.setAttribute('open', true);
      const summary = details.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', true);
      }
    });
  }
};

bindCollapseMobileDetails();

/***
 *
 * Glide Component
 *
 * A slider implementation that can be utilized with only markup knowledge
 *
 * @param {boolean} (optional) data-disable
 * Will stop the slider logic from loading. Useful in scenarios where we only have a single slide e.g. a banner image.
 *
 * @param {number} (optional) data-animation-duration
 * The animation duration when changing slides
 *
 * @param {boolean} (optional) data-auto-rotate
 * Enables/Disables slider auto-rotation
 *
 * @param {number} (optional) data-auto-rotate-duration
 * The delay between changing slides when data-auto-rotate is TRUE
 *
 * @param {number} (optional) data-desktop-columns
 * The number of slides displayed on desktop. This can be a decimal/fraction.
 *
 * @param {number} (optional) data-tablet-columns
 * The number of slides displayed on tablet. This can be a decimal/fraction.
 *
 * @param {number} (optional) data-mobile-columns
 * The number of slides displayed on mobile. This can be a decimal/fraction.
 *
 * @param {number/string} (optional) data-focus-at
 * Where to focus the slider during/after animations. Almost always 0, however 'center' is an option
 *
 * @param {number} (optional) data-start-at
 * Which slide the slider starts at on initialization.
 *
 * @param {number} (optional) data-column-gap
 * How much space is injected between each slide (i.e. the grid)
 *
 * @param {string} (optional) data-pagination-style
 * what type of pagination do we render? (bullets, numbers, arrows)
 *
 * @param {boolean} (optional) data-pagination-desktop
 * Do we render pagination on desktop?
 *
 * @param {boolean} (optional) data-pagination-tablet
 * Do we render pagination on tablet?
 *
 * @param {boolean} (optional) data-pagination-mobile
 * Do we render pagination on mobile?
 *
 * @param {boolean} (optional) data-auto-resize
 * This is only intended for use with the markup in p80-slideshow.
 * This will automatically scale the slideshow height based on the current slide content.
 *
 * @param {boolean} (optional) data-wait-for-render
 * This will stop glide from initializing until it's manually called.
 * Sliders require their containing markup layout to be finalized before they can generate.
 * This parameter becomes useful when content is dynamically generated or injected on page.
 *
 */

class GlideComponent extends HTMLElement {
  static observedAttributes = [
    'data-disable',
    'data-animation-duration',
    'data-auto-rotate',
    'data-auto-rotate-duration',
    'data-desktop-columns',
    'data-tablet-columns',
    'data-mobile-columns',
    'data-focus-at',
    'data-start-at',
    'data-column-gap',
    'data-wait-for-render',
    'data-pagination-style',
    'data-pagination-desktop',
    'data-pagination-tablet',
    'data-pagination-mobile'
  ];

  constructor() {
    super();
    this.connected = false;
    this.bound = false;
  }

  connectedCallback() {
    this.connected = true;
    this.currentMediaQuery = p80.mediaQueryService.currentMediaQuery;
    this.onResizeHandler = this.setHeight.bind(this);

    this.setConfiguration();
    this.setSubscriptions();

    if (this.disable) {
      this.classList.add('glide-component--disabled');
      this.classList.add('glide-component--loaded');
      this.hideLoader();
      if (this.autoResize) {
        document.addEventListener('DOMContentLoaded', this.onResizeHandler);
        this.resizeObserver = new ResizeObserver((entries) => {
          this.onResizeHandler(entries);
        });
        this.resizeObserver.observe(this.slider);
      }
      return;
    }

    // Manual keyboard navigation support - glide's default is "broken"
    this.onKeyUp = this.onKeyUp.bind(this);
    this.addEventListener('keyup', this.onKeyUp);

    const renderTasks = [
      this.initializeGlide.bind(this),
      this.generateBulletPagination.bind(this),
      this.generateArrowPagination.bind(this),
      this.generateNumberPagination.bind(this),
      this.setGlideBindings.bind(this),
      this.mountGlide.bind(this)
    ];

    if (typeof Glide !== 'undefined') {
      this.yieldTasks(renderTasks);
    } else {
      this.onLoadHandler = this.yieldTasks.bind(this, renderTasks);
      document.addEventListener('DOMContentLoaded', this.onLoadHandler);
    }
  }

  disconnectedCallback() {
    this.connected = false;
    if (this.glideUpdateUnsubscriber) {
      this.glideUpdateUnsubscriber();
    }
    if (this.mediaQueryChangeUnsubscriber) {
      this.mediaQueryChangeUnsubscriber();
    }
    this.resizeObserver?.disconnect();
    this.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('DOMContentLoaded', this.onLoadHandler);
    if (this.glide) {
      try {
        this.glide.destroy();
      } catch (e) {
        console.info(e);
      }
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.connected) return;
    const tasks = [
      this.setConfiguration.bind(this),
      this.initializeGlide.bind(this),
      this.generateBulletPagination.bind(this),
      this.generateArrowPagination.bind(this),
      this.generateNumberPagination.bind(this),
      this.setGlideBindings.bind(this),
      this.mountGlide.bind(this)
    ];
    if (oldValue !== newValue) this.yieldTasks(tasks);
  }

  yieldToMain() {
    return new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
  }

  async yieldTasks(tasks) {
    while (tasks.length > 0) {
      const task = tasks.shift();
      task();
      await this.yieldToMain();
    }
  }

  setSubscriptions() {
    this.glideUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.glideUpdate, (event) => {
      if (event.id !== this.id) return;
      this.onUpdateBroadcast(event);
    });

    this.mediaQueryChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.mediaQueryChange, (event) => {
      this.onMediaQueryChange(event);
    });
  }

  setConfiguration() {
    this.showLoader();
    this.classList.remove('glide-component--loaded');
    if (this.glide) {
      try {
        this.glide.destroy();
      } catch (e) {
        console.info(e);
      }
    }

    // Internal configs
    this.disable = this.getAttribute('data-disable') === 'true' ? true : false;
    this.slider = this.querySelector('.glide');
    this.slides = this.querySelectorAll('.glide__slides > li');
    this.glide = null;
    this.loader = this.querySelector('.slideshow__loader');
    this.id = this.getAttribute('data-id') ? this.getAttribute('data-id') : null;

    // Slider Configs
    const animationDuration = this.getAttribute('data-duration');
    this.animationDuration = !Number.isNaN(parseFloat(animationDuration, 10)) ? parseFloat(animationDuration, 10) : 400;

    this.autoRotate = this.getAttribute('data-auto-rotate') === 'true' ? true : false;
    const autoRotateDuration = this.getAttribute('data-auto-rotate-duration');
    this.autoRotateDuration = !Number.isNaN(parseFloat(autoRotateDuration, 10))
      ? parseFloat(autoRotateDuration, 10)
      : 0;

    const desktopColumns = this.getAttribute('data-desktop-columns');
    // This has to be a text field for decimal places - validate the user hasn't put in actual text before proceeding
    this.desktopColumns = !Number.isNaN(parseFloat(desktopColumns, 10)) ? parseFloat(desktopColumns, 10) : 1;

    // This has to be a text field for decimal places - validate the user hasn't put in actual text before proceeding
    const tabletColumns = this.getAttribute('data-tablet-columns');
    this.tabletColumns = !Number.isNaN(parseFloat(tabletColumns, 10)) ? parseFloat(tabletColumns, 10) : 1;

    const mobileColumns = this.getAttribute('data-mobile-columns');
    // This has to be a text field for decimal places - validate the user hasn't put in actual text before proceeding
    this.mobileColumns = !Number.isNaN(parseFloat(mobileColumns, 10)) ? parseFloat(mobileColumns, 10) : 1;

    const focusAt = this.getAttribute('data-focus-at');
    // focusAt can be an integer or string per glide docs - e.g. 'center'
    this.focusAt =
      focusAt === 'center' ? focusAt : !Number.isNaN(parseFloat(focusAt, 10)) ? parseFloat(focusAt, 10) : 0;

    const startAt = this.getAttribute('data-start-at');
    this.startAt = !Number.isNaN(parseInt(startAt, 10)) ? parseInt(startAt, 10) : 0;

    const columnGap = this.getAttribute('data-column-gap');
    this.columnGap = !Number.isNaN(parseInt(columnGap, 10)) ? parseInt(columnGap, 10) : 24;

    // This is only intended for use via p80-slideshow
    this.autoResize = this.getAttribute('data-auto-resize') === 'true' ? true : false;

    this.waitForRender = this.getAttribute('data-wait-for-render') === 'true' ? true : false;

    this.desktopPagination = this.getAttribute('data-pagination-desktop') === 'true' ? true : false;
    this.tabletPagination = this.getAttribute('data-pagination-tablet') === 'true' ? true : false;
    this.mobilePagination = this.getAttribute('data-pagination-mobile') === 'true' ? true : false;

    this.paginationStyles = {
      bullets: 'bullets',
      numbers: 'numbers',
      arrows: 'arrows'
    };
    const paginationStyle = this.getAttribute('data-pagination-style');
    this.paginationStyle = this.paginationStyles[paginationStyle] ? this.paginationStyles[paginationStyle] : null;
  }

  onMediaQueryChange(event) {
    if (!event || !event.currentMedia) return;
    this.currentMediaQuery = event.currentMedia;
  }

  // This is only intended for use via p80-slideshow
  setHeight() {
    const activeSlide = this.glide ? this.slides[this.glide.index] : this.slides[0];
    const activeMedia = activeSlide?.querySelectorAll('.slideshow__image');
    const activeText = activeSlide?.querySelector('.slideshow__content');
    const activeTextHeight = activeText ? activeText.scrollHeight : 0;
    const activeBreakpointMedia =
      this.currentMediaQuery.type === p80.mediaQueryService.mediaTypes.small && activeMedia[1]
        ? activeMedia[1]
        : activeMedia[0]
        ? activeMedia[0]
        : null;
    const activeImageHeight = activeBreakpointMedia?.offsetHeight;

    if (!activeTextHeight || !activeImageHeight) return;

    const finalHeight = activeTextHeight > activeImageHeight ? activeTextHeight : activeImageHeight;

    this.style.setProperty('--height', `${finalHeight}px`);
  }

  onKeyUp(event) {
    if (!this.glide) return;
    const code = event.code ? event.code : event.keyCode;
    const right = [39, 'ArrowRight'];
    const left = [37, 'ArrowLeft'];

    const direction = right.includes(code) ? '>' : left.includes(code) ? '<' : null;
    if (!direction) return;

    this.glide.go(direction);
  }

  generateBulletPagination() {
    const bullets = this.querySelector('.glide__bullets');
    bullets?.remove();
    this.bullets = null;

    if (
      (this.paginationStyle !== this.paginationStyles.arrows &&
        this.paginationStyle !== this.paginationStyles.bullets) ||
      (this.paginationStyle === this.paginationStyles.arrows && !this.mobilePagination)
    ) {
      return;
    }

    const container = document.createElement('div');
    container.classList.add('glide__bullets');
    container.setAttribute('data-glide-el', 'controls[nav]');
    if (!this.desktopPagination || this.paginationStyle === this.paginationStyles.arrows)
      container.classList.add('large-up-hide');
    if (!this.tabletPagination && this.paginationStyle !== this.paginationStyles.arrows)
      container.classList.add('medium-hide');
    if (!this.mobilePagination && this.paginationStyle !== this.paginationStyles.arrows)
      container.classList.add('small-hide');

    const desktopBullets = this.slides.length - (Math.floor(this.desktopColumns) - 1);
    const tabletBullets = this.slides.length - (Math.floor(this.tabletColumns) - 1);
    const mobileBullets = this.slides.length - (Math.floor(this.mobileColumns) - 1);
    this.slides.forEach((slide, index) => {
      const count = index + 1;
      const bullet = document.createElement('button');
      if (count > desktopBullets) bullet.classList.add('large-up-hide');
      if (count > tabletBullets) bullet.classList.add('medium-hide');
      if (count > mobileBullets) bullet.classList.add('small-hide');
      bullet.classList.add('glide__bullet');
      bullet.setAttribute('data-glide-dir', `=${index}`);
      bullet.setAttribute('title', `Slide ${count}`);
      container.appendChild(bullet);
    });

    this.querySelector('.glide').appendChild(container);
    this.bullets = this.querySelector('.glide__bullets');
  }

  generateNumberPagination() {
    const pagination = this.querySelector('.glide__pagination');
    const oldPreviousButton = pagination?.querySelector(`[aria-label="Previous"]`);
    const oldNextButton = pagination?.querySelector(`[aria-label="Next"]`);
    oldPreviousButton?.removeEventListener('click', this.previousSlideHandler);
    oldNextButton?.removeEventListener('click', this.nextSlideHandler);
    pagination?.remove();
    this.pagination = null;

    if (this.paginationStyle !== this.paginationStyles.numbers) return;

    const container = document.createElement('div');
    container.classList.add('glide__pagination');
    if (!this.desktopPagination) container.classList.add('large-up-hide');
    if (!this.tabletPagination) container.classList.add('medium-hide');
    if (!this.mobilePagination) container.classList.add('small-hide');

    const desktopPages = Math.max(this.slides.length - (Math.floor(this.desktopColumns) - 1), 1);
    const tabletPages = Math.max(this.slides.length - (Math.floor(this.tabletColumns) - 1), 1);
    const mobilePages = Math.max(this.slides.length - (Math.floor(this.mobileColumns) - 1), 1);

    const prevButton = document.createElement('button');
    prevButton.setAttribute('type', 'button');
    prevButton.setAttribute('aria-label', 'Previous');
    prevButton.innerHTML = window.icons.caret;
    prevButton.addEventListener('click', this.previousSlideHandler);
    container.appendChild(prevButton);

    const count = document.createElement('p');
    count.setAttribute('data-count', true);
    const defaultCount = document.createElement('span');
    defaultCount.textContent = this.startAt + 1;
    container.appendChild(defaultCount);

    const divider = document.createElement('span');
    divider.textContent = '/';
    container.appendChild(divider);

    const desktop = document.createElement('span');
    desktop.classList.add('small-hide', 'medium-hide');
    desktop.textContent = `${desktopPages}`;
    container.appendChild(desktop);

    const tablet = document.createElement('span');
    tablet.classList.add('large-up-hide', 'small-hide');
    tablet.textContent = `${tabletPages}`;
    container.appendChild(tablet);

    const mobile = document.createElement('span');
    mobile.classList.add('large-up-hide', 'medium-hide');
    mobile.textContent = `${mobilePages}`;
    container.appendChild(mobile);

    const nextButton = document.createElement('button');
    nextButton.setAttribute('type', 'button');
    nextButton.setAttribute('aria-label', 'Next');
    nextButton.innerHTML = window.icons.caret;
    nextButton.addEventListener('click', this.nextSlideHandler);
    container.appendChild(nextButton);

    this.querySelector('.glide').appendChild(container);
    this.pagination = this.querySelector('.glide__pagination');
  }

  generateArrowPagination() {
    const arrows = this.querySelector('.glide__controls');
    arrows?.remove();
    this.arrows = null;

    if (this.paginationStyle !== this.paginationStyles.arrows) return;

    const container = document.createElement('div');
    container.classList.add('glide__controls');
    container.setAttribute('data-glide-el', 'controls');
    if (!this.desktopPagination) container.classList.add('large-up-hide');
    container.classList.add('medium-hide');
    // We will never display these on mobile due to space constraints
    container.classList.add('small-hide');

    const previous = document.createElement('button');
    previous.setAttribute('type', 'button');
    previous.classList.add('glide__previous');
    previous.setAttribute('data-glide-dir', '<');
    previous.setAttribute('aria-label', window.accessibilityStrings.previousSlide);
    previous.innerHTML = window.icons.caret;
    container.appendChild(previous);

    const next = document.createElement('button');
    next.setAttribute('type', 'button');
    next.classList.add('glide__next');
    next.setAttribute('data-glide-dir', '>');
    next.setAttribute('aria-label', window.accessibilityStrings.nextSlide);
    next.innerHTML = window.icons.caret;
    container.appendChild(next);
    this.querySelector('.glide').appendChild(container);
    this.arrows = this.querySelector('.glide__controls');
  }

  initializeGlide() {
    const component = this;
    this.glide = new Glide(component.slider, {
      type: 'slider',
      bound: true,
      animationDuration: component.animationDuration,
      focusAt: component.focusAt,
      startAt: component.startAt,
      perView: component.desktopColumns,
      gap: component.columnGap,
      autoplay: component.autoRotate ? component.autoRotateDuration : false,
      // Glide binds this to a global listener - using default keyboard navigation controls *every* slider on the page, regardless of focus
      // We create a manual scoped binding instead
      keyboard: false,
      breakpoints: {
        750: {
          perView: component.mobileColumns
        },
        990: {
          perView: component.tabletColumns
        },
        // These resolve an unfixed Glide bug
        5000: {
          perView: component.desktopColumns
        },
        9999: {
          perview: component.desktopColumns
        }
      }
    });
    this.nextSlideHandler = () => this.glide.go('>');
    this.previousSlideHandler = () => this.glide.go('<');
  }

  setGlideBindings() {
    this.glide.on('mount.after', () => {
      this.glide.isMounted = true;
    });

    this.glide.on('move.after', () => {
      const currentSlideIndicator = this.pagination?.querySelector('span');
      if (currentSlideIndicator) Math.max((currentSlideIndicator.textContent = this.glide.index + 1), 1);
      if (this.autoResize) {
        this.onResizeHandler();
      }
    });

    if (this.autoResize) {
      this.glide.on('resize', () => {
        this.onResizeHandler();
      });
    }

    this.glide.on('build.after', () => {
      if (this.autoResize) {
        this.onResizeHandler();
      }
      this.classList.add('glide-component--loaded');
      this.hideLoader();
      this.showPagination();
    });
  }

  mountGlide() {
    this.glide.mount();
  }

  showPagination() {
    this.bullets?.classList.add('glide__bullets--visible');
    this.pagination?.classList.add('glide__pagination--visible');
    this.arrows?.classList.add('glide__controls--visible');
  }

  hidePagination() {
    this.bullets?.classList.remove('glide__bullets--visible');
    this.pagination?.classList.remove('glide__pagination--visible');
    this.arrows?.classList.remove('glide__controls--visible');
  }

  showLoader() {
    this.loader?.classList.add('slideshow__loader--visible');
  }

  hideLoader() {
    this.loader?.classList.remove('slideshow__loader--visible');
  }

  onUpdateBroadcast(event) {
    if (this.glide && this.glide.isMounted) return;
    if (this.waitForRender) {
      this.waitForRender = false;
      if (!this.slides || !this.slides.length) {
        this.slides = this.querySelectorAll('.glide__slides > li');
      }
      const tasks = [
        this.initializeGlide.bind(this),
        this.generateBulletPagination.bind(this),
        this.generateArrowPagination.bind(this),
        this.generateNumberPagination.bind(this),
        this.setGlideBindings.bind(this),
        this.mountGlide.bind(this)
      ];
      this.yieldTasks(tasks);
    }
  }
}

customElements.define('glide-component', GlideComponent);
